import { useEffect, useMemo, useRef, useState } from "react";
import { Box, Chip, Stack, Typography } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import RestaurantIcon from "@mui/icons-material/Restaurant";
import { colors } from "brand";
import { TermReasonChip } from "common-fe";
import { useTranslation } from "i18n-fe";
import { RIDE_STATUSES, TERM_REASONS, isScoreSuppressed, sortJudgesForDisplay } from "score-common";
import { useAnnouncerContext } from "../context/AnnouncerContext";
import { useRingData } from "../context/RingDataContext";
import ActiveAlert from "./ActiveAlert";
import {
  selectLiveDeskSequence,
  findRideResult,
  isRideResultPending,
  describeJustFinishedMode,
  describeJustFinishedPlacing,
  describeInTheRingState,
  describeCommittedJudges,
  findActiveClassLabel,
  ringHasRidesLeftToday,
  rideOrdinalInClass,
  IN_THE_RING_STATE,
  JUST_FINISHED_MODE,
} from "../utils/liveDesk";
import { formatTime } from "../utils/formatTime";
import { ROW_KIND } from "../utils/orderOfGo";
import { formatOrdinal } from "../utils/formatOrdinal";
import { formatComposite } from "../utils/formatComposite";
import { formatElapsedSeconds } from "../utils/presence";
import { termChipReason, termChipLabels } from "../utils/termChip";
import SectionTag from "./SectionTag";
import RideDetailBlock from "./RideDetailBlock";
import { formatClassNumber } from "../utils/formatClassNumber";

// #2149 (docs/score/announcer-ui-redesign.md §4.3) — band 3, the live desk.
// Three FIXED slots (JUST FINISHED / IN THE RING NOW / NEXT UP) at fixed
// heights, at every state of the data (Rule 1 + Rule 3). Consumes ONLY
// useAnnouncerContext()/useRingData() — no new hook, no new poll (#2137's
// whole point, gotcha 2). Slot 1's score comes PASSIVELY off
// `resultsByClassId`, which is lifted from whichever ClassResults instance
// mounted that class (later cards #2141/#2142) — it stays empty until then
// by design, not a bug, so Slot 1 correctly reads "score pending" until a
// look-up band exists to populate it.
//
// No "entered ring" timestamp exists anywhere in the codebase distinct from
// `ride.scheduledTime` (confirmed during design research, #2149 handoff) —
// so `entered {{time}}` is `formatTime(currentRow.scheduledTime)` and
// `in the ring {{elapsed}}` is `liveNow - scheduledTime` via the existing
// `formatElapsedSeconds` (utils/presence.js). This is a substitution, not
// the literal ring-entry event, documented here rather than re-litigated.
//
// Likewise there is no "ring resumes at" field on a ring-state BREAK row
// (score-common's RingState schema carries no such value) — `back at` for
// the ON BREAK state line is read off the first NEXT UP row's own
// scheduledTime (the next ride still due once the ring picks back up),
// which is real data already on the row, not invented. Same design-decision
// category as the entered-ring substitution above, not a federation-rule
// question.

// #3397 -- the redesigned centre column. JUST FINISHED keeps a fixed height per
// tier; NEXT UP is exactly the next three rides at their natural height (no
// scroll, no lookahead window); IN THE RING NOW takes ALL the remaining height
// (flex) and must never scroll, so the tier thresholds below are budgets for
// the three boxes' estimated natural heights, measured against the band's OWN
// rendered height (ResizeObserver), never a window-height guess (#2145).
const SLOT1_HEIGHT = 176;
const SLOT1_HEIGHT_COMPACT = 140;
const SLOT1_HEIGHT_SMALL = 92;
// #3641 -- a scored ride with judge scores needs 128 at the small tier: header 24 + score 46px + a
// full judges row (15px label over 20px score) + padding, so 4-5 judge scores fit at 20px bold
// (Jaybill, 2026-10-22, option A, raise the small slot). Only that state grows: an empty, pending,
// terminated or place-only box stays 92 so In the Ring Now keeps the height it was budgeted.
const SLOT1_HEIGHT_SMALL_JUDGES = 128;
// The tier thresholds are the band-height budgets #2447/#2624 tuned and the
// live-desk.cy.js geometry specs pin (1280x900 normal, 1000x660 compact,
// 1280x600 / 1000x560 small); they are deliberately unchanged so a viewport keeps
// the tier it always had. At 1280x760 (~592px band, compact) the three boxes
// (140 + a flexed In the Ring Now + 3 compact NEXT UP rows) hold a full detail
// set with room to spare; below that, the tail of the box clips (the judges row
// sits high in the box so the live information is the last thing to go).
const NORMAL_TOTAL_HEIGHT = 650;
const COMPACT_TOTAL_HEIGHT = 464;

// How many upcoming rides NEXT UP shows (#3397: up to three, fewer when fewer
// are left). The data selector is capped at the same number so the DOM never
// holds more.
const NEXT_UP_COUNT = 3;
// #3398 -- at the compact and small tiers (the short viewports) NEXT UP drops to two rows so In the
// Ring Now, which takes whatever height is left, can hold a full detail set (Jaybill, 2026-10-15).
const NEXT_UP_COUNT_SHORT = 2;
// #3641 -- at the SMALL tier only, NEXT UP drops again to one row. JUST FINISHED grows to 128px there
// when a scored ride shows 4-5 judge scores at 20px, and that height comes out of In the Ring Now
// (the flex box), so NEXT UP gives up its second row to keep the #3398 no-clip rule (Jaybill,
// 2026-10-22, option 1). Compact keeps two rows, normal keeps three.
const NEXT_UP_COUNT_SMALL = 1;

function exceptionStatusLabel(row, t) {
  if (!row) return null;
  // AC11 precedence: ELIMINATED wins over whatever raw termReason string
  // would otherwise show (#1838 precedent) — score-common's TERM_REASONS,
  // USEF DR123.3 vocabulary (docs/rules/usef/rulebook-dr-dressage-2026.pdf,
  // #2052/#2053).
  if (row.termReason === TERM_REASONS.ELIMINATED) return t("liveDesk.eliminated");
  if (row.status === RIDE_STATUSES.SCRATCHED) return row.termReason || t("oog.scratched");
  // Card #2840 — ANY other resolved term reason (No Show, Excused, or a
  // Scratched not yet reflected on row.status) must also short-circuit
  // here, exactly like ELIMINATED/SCRATCHED above: without this, a No
  // Show/Excused ride falls through to the pending/score branch below,
  // and result.status for those reads FINAL (not PARTIAL/CONFIRMED,
  // buildRideResult's own ride-level short-circuit), so isRideResultPending
  // reads false and the score Typography renders — relying only on
  // result.composite already being null to avoid a fabricated number. That
  // holds for the ride-level No Show/Excused branches (score-api results.js,
  // #2840 AC5), but this checks row.termReason (deriveRideTermReason,
  // orderOfGo.js) directly rather than trust a downstream API value for a
  // fact this component already has independently.
  if (isScoreSuppressed(row.termReason)) return row.termReason;
  // An ordinary unplaced ride shows NO status text (AC11).
  return null;
}

// #3636 -- the JUST FINISHED box, "ancient judge on a small screen": no text under 15px, nothing
// lighter than #333, left-aligned, ONE layout for every judge count. Dark orange (not the brand
// foxOrange) so white-on-orange and orange-on-white both clear 4.5:1.
const JF_ORANGE = "#A85A00";
const JF_INK = "#111";
const JF_SECONDARY = "#333";
const JF_RULE = "#bdbdbd";
const TABULAR = { fontVariantNumeric: "tabular-nums" };

// The one status chip (header, right). Placing wins once placed; else Confirmed (AC10: check icon
// PLUS the word, never colour alone). Deliberately not an MUI Chip: AC9 pins the box as plain text.
function PlacingText({ result, t }) {
  const mode = describeJustFinishedMode(result);
  if (mode === JUST_FINISHED_MODE.FINAL) {
    // AC12 -- result.placing === 0 is STORED DATA meaning UNPLACED, never an
    // index to add 1 to. describeJustFinishedPlacing owns that decision.
    const { unplaced, placing } = describeJustFinishedPlacing(result);
    const placingText = unplaced ? t("results.unplacedLabel") : formatOrdinal(placing);
    return (
      <Box
        component="span"
        data-testid="live-desk-just-finished-placing"
        sx={{
          display: "inline-block",
          flexShrink: 0,
          px: 1.25,
          borderRadius: 999,
          bgcolor: JF_ORANGE,
          color: "#fff",
          fontSize: 17,
          fontWeight: 900,
          lineHeight: 1.5,
          whiteSpace: "nowrap",
        }}
      >
        {placingText}
      </Box>
    );
  }
  if (mode === JUST_FINISHED_MODE.CONFIRMED) {
    return (
      <Box
        component="span"
        data-testid="live-desk-just-finished-confirmed"
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 0.5,
          flexShrink: 0,
          pl: 0.75,
          pr: 1.25,
          borderRadius: 999,
          border: `2px solid ${JF_ORANGE}`,
          color: JF_ORANGE,
          fontSize: 16,
          fontWeight: 700,
          lineHeight: 1.4,
          whiteSpace: "nowrap",
        }}
      >
        <CheckCircleIcon sx={{ fontSize: 18 }} />
        {t("liveDesk.confirmed")}
      </Box>
    );
  }
  return null;
}

// "<rider> on <horse>"; only one of the two known shows just that one, no "on".
function riderOnHorseLine(row, t) {
  const rider = row?.riderName;
  const horse = row?.horseName;
  if (rider && horse) return t("liveDesk.riderOnHorse", { rider, horse });
  return rider || horse || "";
}

// #2443 -- the per-judge scores RideRow carried (CONFIRMED + FINAL). #3636: a grid of N equal cells,
// N = judges with a number; each cell is position label (15px bold #333) over score (20px bold,
// tabular). Only a judge with a numeric percentage gets a cell: a DSHB place-only sheet carries
// none (#2183 AC5), and a cell with no number would be a fabricated "E 0.000". PARTIAL never
// reaches here (isRideResultPending), so no number is shown for a ride score-api is still withholding.
// #3636 AC3 -- a judge score is 20px bold at EVERY tier; a short tier drops a whole row, never text size.
const JUDGE_SCORE_PX = 20;
function JudgeScoreChips({ result, t }) {
  const judges = sortJudgesForDisplay(result?.perJudge ?? [], (j) => j.position).filter((j) =>
    Number.isFinite(j.percentage),
  );
  if (judges.length === 0) return null;
  return (
    <Box
      data-testid="live-desk-just-finished-judge-scores"
      data-judge-count={judges.length}
      style={{ gridTemplateColumns: `repeat(${judges.length}, 1fr)` }}
      sx={{
        display: "grid",
        minWidth: 0,
        overflow: "hidden",
        width: "100%",
        borderTop: `1px solid ${JF_RULE}`,
        pt: 0.5,
      }}
    >
      {judges.map((j, i) => {
        // #3369 -- a judge whose ring position score-api couldn't resolve used
        // to fall back to the opaque judgeId (a ~36-char UUID), which blew the
        // chip out to several times the width of a one-letter position. Label it by its ordinal
        // in the displayed panel ("J1".."J5") instead: short, unique, announceable.
        const label = j.position || `J${i + 1}`;
        return (
          <Box
            key={j.judgeId || j.position || `idx-${i}`}
            data-testid="live-desk-judge-score-chip"
            data-position={j.position || undefined}
            aria-label={t("results.judgeScore", {
              position: label,
              value: formatComposite(j.percentage),
            })}
            sx={{ textAlign: "center", minWidth: 0, whiteSpace: "nowrap" }}
          >
            <Box component="span" sx={{ display: "block", fontSize: 15, fontWeight: 700, color: JF_SECONDARY, lineHeight: 1.2 }}>
              {label}
            </Box>{" "}
            <Box
              component="span"
              sx={{
                display: "block",
                fontSize: JUDGE_SCORE_PX,
                fontWeight: 700,
                color: JF_INK,
                lineHeight: 1.2,
                ...TABULAR,
              }}
            >
              {formatComposite(j.percentage)}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}

function JustFinishedSlot({ row, resultsByClassId, height, py, tier, loading, t }) {
  const result = row ? findRideResult(resultsByClassId, row) : null;
  const pending = row ? isRideResultPending(result) : true;
  const exceptionLabel = exceptionStatusLabel(row, t);
  // #3399 -- a terminated ride shows a red term-reason chip, never a strikethrough.
  const chipReason = termChipReason(row);
  const scored = Boolean(row) && !chipReason && !exceptionLabel && !pending;
  const small = tier === "small";
  const compact = tier === "compact";
  const who = row ? riderOnHorseLine(row, t) : "";
  // Rows drop in spec order, never text size: the rider/horse line goes first at the compact and
  // small tiers once a score + judges need the room; unscored states have room at compact only.
  // #3641 -- the judges row (15px label over 20px score) sits under the score at every tier,
  // including small, whose box grows to 128px to hold 4-5 judges. Never smaller text.
  const showJudges = (result?.perJudge ?? []).some((j) => Number.isFinite(j.percentage));
  // The compact tier reclaims vertical padding (not text) so score + judges fit the 140px box.
  const padY = compact ? 0.75 : py;
  const showWho = Boolean(who) && (scored ? !compact && !small : !small);
  const boxHeight = small && scored && showJudges ? SLOT1_HEIGHT_SMALL_JUDGES : height;
  const statusChip = scored ? <PlacingText result={result} t={t} /> : null;
  const bodyText = { fontSize: 15, fontWeight: 700, color: JF_SECONDARY };

  return (
    <Box
      data-testid="live-desk-just-finished"
      sx={{
        height: boxHeight,
        flexShrink: 0,
        overflow: "hidden",
        px: 2,
        py: padY,
        textAlign: "left",
        borderBottom: 1,
        borderColor: "divider",
      }}
    >
      <Stack
        data-testid="live-desk-just-finished-header"
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={1}
        sx={{ minHeight: small ? 24 : 28 }}
      >
        <Typography
          component="div"
          noWrap
          sx={{ fontSize: 20, fontWeight: 700, color: JF_INK, lineHeight: 1.3, minWidth: 0 }}
        >
          {t("liveDesk.justFinishedTitle")}
          {row?.competitorNumber ? (
            <>
              {": "}
              <Box component="span" sx={{ fontWeight: 900 }}>
                {t("liveDesk.justFinishedCno", { number: row.competitorNumber })}
              </Box>
              <SectionTag section={row.section} sx={{ color: JF_SECONDARY, fontSize: 15, fontWeight: 700 }} />
            </>
          ) : null}
        </Typography>
        {statusChip}
      </Stack>
      {!row && loading ? (
        <Typography data-testid="live-desk-just-finished-loading" role="status" sx={bodyText}>
          {t("liveDesk.justFinishedLoading")}
        </Typography>
      ) : !row ? (
        <Typography sx={bodyText}>{t("liveDesk.empty")}</Typography>
      ) : (
        <Stack spacing={0}>
          {showWho ? (
            <Typography
              data-testid="live-desk-just-finished-who"
              noWrap
              sx={{ fontSize: 20, fontWeight: 700, color: JF_INK, lineHeight: 1.3 }}
            >
              {who}
            </Typography>
          ) : null}
          {chipReason ? (
            <Box sx={{ pt: 0.5 }}>
              <TermReasonChip
                data-testid="live-desk-just-finished-status"
                termReason={chipReason}
                termLabels={termChipLabels(t)}
                sx={{ fontWeight: 700 }}
              />
            </Box>
          ) : exceptionLabel ? (
            <Typography
              data-testid="live-desk-just-finished-status"
              sx={{ fontSize: 20, fontWeight: 700, color: JF_INK, pt: 0.5 }}
            >
              {exceptionLabel}
            </Typography>
          ) : pending ? (
            <Typography data-testid="live-desk-score-pending" sx={{ fontSize: 24, fontWeight: 700, color: JF_SECONDARY, pt: 0.5 }}>
              {t("liveDesk.scorePending")}
            </Typography>
          ) : (
            <>
              {/* AC9 -- plain TEXT, no chiclet/bubble. Score dominant (46px/900, "%" 26px); the judges
                  sit on their own row under it at every tier (#3641). */}
              <Box sx={{ display: "flex", alignItems: "center", minWidth: 0 }}>
                <Typography
                  data-testid="live-desk-just-finished-score"
                  component="div"
                  sx={{
                    flexShrink: 0,
                    fontSize: 46,
                    fontWeight: 900,
                    color: JF_INK,
                    lineHeight: 1.05,
                    ...TABULAR,
                  }}
                >
                  {/* Card #2840 belt-and-braces: exceptionStatusLabel above already routes every
                      row.termReason case away from this branch, but this is the one place a real
                      (possibly 0) composite could otherwise print, so it re-checks
                      isScoreSuppressed directly rather than trust result.composite alone. */}
                  {!isScoreSuppressed(row.termReason) && formatComposite(result?.composite) ? (
                    <>
                      {formatComposite(result.composite)}
                      <Box component="span" sx={{ fontSize: 26, fontWeight: 900 }}>
                        %
                      </Box>
                    </>
                  ) : (
                    "—"
                  )}
                </Typography>
              </Box>
              {showJudges ? <JudgeScoreChips result={result} t={t} /> : null}
            </>
          )}
        </Stack>
      )}
    </Box>
  );
}

function InTheRingSlot({
  currentRow,
  ringState,
  selectedRing,
  liveNow,
  nextUpRows,
  rows,
  activeClassLabel,
  py,
  compact,
  small,
  ridesLoading,
  ringStateKnown,
  ringHasRidesLeft,
  hasFetchError,
  presenceRows,
  resultsByClassId,
  t,
}) {
  const stateLine = useMemo(() => {
    if (currentRow) return null;
    // #2703 / #2449 -- no current ride AND the rides fetch or the ring-state
    // fetch has not answered: the app does not know yet, so never print the
    // confident "no class is in progress" (or any other settled) line. Only
    // when there is no current row: a ride that has already landed paints
    // at once, whatever the ring-state fetch is doing.
    // #3167 -- a failed fetch with NO ring selected: when GET /class fails no
    // ring list resolves, so no ring is ever selected and the
    // `ringStateKnown || !selectedRing` override below treats the ring state
    // as "known" and would fall through to the confident "no class in
    // progress" off a failed fetch (the #2233 false-confident shape).
    // Say so instead. Only with no ring selected: with a ring selected the
    // ring-state answer is still real, and #2780's END_OF_DAY gate handles
    // the rides-fetch case.
    if (hasFetchError && !selectedRing) return t("liveDesk.couldNotLoad");
    if (ridesLoading) return t("liveDesk.currentRideLoading");
    if (!ringStateKnown) return t("liveDesk.ringStateLoading");
    const state = describeInTheRingState(ringState?.status);
    if (state === IN_THE_RING_STATE.BREAK) {
      // #3448 -- slot 0 may now be the break itself, so "back at" reads the
      // first RIDE in NEXT UP, never a break row.
      const nextRide = nextUpRows?.find((r) => r.kind === ROW_KIND.RIDE);
      const backAt = nextRide?.scheduledTime ? formatTime(nextRide.scheduledTime, nextRide.scheduledTimeZone) : null;
      return backAt ? t("liveDesk.onBreakUntil", { time: backAt }) : t("liveDesk.onBreak");
    }
    if (state === IN_THE_RING_STATE.END_OF_DAY) {
      // #2780 -- END_OF_DAY is only believed when the ride list agrees: the
      // ring has no ride left today AND the rides/breaks fetch has not
      // failed (an errored fetch leaves `rows` empty, which is "unknown",
      // never "finished"). Otherwise it reads like a class ended, not a day.
      if (ringHasRidesLeft || hasFetchError) return t("liveDesk.noClassInProgress");
      return t("liveDesk.ringDone", { ring: ringState?.ring ?? selectedRing ?? "" });
    }
    // #2129 — End class cleared the ring's class but the day is not over:
    // the same "no class in progress" line a ring with no state shows.
    if (state === IN_THE_RING_STATE.CLASS_ENDED) {
      return t("liveDesk.noClassInProgress");
    }
    // #2587 (TRI-241 judge finding A) — RUNNING with no CURRENT row is the
    // class the daysheet still lists as in progress, between one ride's
    // confirmation and the next one starting (or, for a class's last ride,
    // until the next class starts) — never the same "nothing is happening"
    // message a genuine break/end-of-day/no-ring-state gets.
    // #2585 (TRI-238) — name that class when it's known (same classId the
    // sidebar's RUNNING marker uses, findActiveClassLabel above), rather
    // than leaving the sidebar's highlighted class unnamed here.
    if (state === IN_THE_RING_STATE.BETWEEN_RIDES) {
      return activeClassLabel
        ? t("liveDesk.betweenRidesWithClass", { classLabel: activeClassLabel })
        : t("liveDesk.betweenRides");
    }
    return t("liveDesk.noClassInProgress");
  }, [currentRow, ringState, nextUpRows, selectedRing, activeClassLabel, ridesLoading, ringStateKnown, ringHasRidesLeft, hasFetchError, t]);

  // #2631 AC4 — a judge whose name never resolved (buildJudges' ladder,
  // orderOfGo.js) renders the position ALONE, not "B: " with a dangling
  // colon and nothing after it. Static key either way: judgeAtPosition when
  // there's a name to interpolate, the bare position string when there isn't.
  const judgesText = (currentRow?.judges || [])
    .map((j) => (j.name ? t("liveDesk.judgeAtPosition", { position: j.position, name: j.name }) : j.position || ""))
    .filter(Boolean)
    .join(", ");

  // #2443 -- per-judge committed state, only while the current ride's result
  // is PARTIAL. Rendered INSIDE the judges line (a check beside each judge who
  // has confirmed), never as a new line: the In the Ring Now box never scrolls and is tight.
  const committed = useMemo(
    () => describeCommittedJudges(currentRow?.judges, currentRow ? findRideResult(resultsByClassId, currentRow) : null),
    [currentRow, resultsByClassId],
  );

  // #2446 -- quiet seats first: the row never wraps and clips on its RIGHT
  // edge (the chip Stack is flex-start; the parent's space-between does the
  // right-aligning while it fits), so if a crowd
  // of seats ever overflows the title line, it is a healthy seat at the end
  // that gets clipped, never the quiet one at the front (the whole point of
  // the signal). A flex-end row would clip the LEFT edge instead.
  const sortedPresence = useMemo(
    () => [...(presenceRows || [])].sort((a, b) => Number(b.quiet) - Number(a.quiet) || a.seat.localeCompare(b.seat)),
    [presenceRows],
  );

  // #3172 -- C's bell (ENTERING_RING) makes the ride CURRENT before the horse
  // is in the ring (IN_PROGRESS). The title and the elapsed counter must not
  // say "in the ring" for a horse still walking in: a status-keyed STATIC title
  // (same text length class, so the box height is untouched) and no
  // elapsed counter until IN_PROGRESS.
  const isEntering = currentRow?.status === RIDE_STATUSES.ENTERING_RING;

  const elapsedSeconds = currentRow?.scheduledTime && !isEntering
    ? Math.max(0, (liveNow - Date.parse(currentRow.scheduledTime)) / 1000)
    : null;

  // #3169 -- below the normal tier the In the Ring Now box has less height and a
  // ride with the judges row plus the full detail block needs more than MUI's
  // default leading (body2 1.43, caption 1.66) allows. Tightening the leading to
  // 1.2 on the detail lines, together with the 0 gaps below, saves height without
  // dropping a line.
  const tightLine = compact ? { lineHeight: 1.2 } : undefined;
  // #3392 -- the desk is 448px now (was 560px): a 5-seat panel can no longer afford the
  // words "judges confirmed" on the committed row and still leave each unconfirmed seat
  // room for its check + position, so the dense row prints a bare "2/5" and carries the
  // full phrase on the aria-label.
  const denseCommitted = committed ? committed.entries.length > 3 : false;

  const ordinal = useMemo(() => rideOrdinalInClass(rows, currentRow), [rows, currentRow]);
  // The ride-detail lines (12px, 11px at small, leading 1) at the shorter tiers so the whole
  // set fits without scrolling now that SIRE/DAM/DAM'S SIRE are 3 rows (#3409); MUI's default body2 at normal.
  const detailSx = compact
    ? { "& .MuiTypography-body2": { fontSize: small ? "0.7rem" : "0.75rem", lineHeight: 1 } }
    : { "& .MuiTypography-body2": { lineHeight: 1.3 } };

  const classLine = currentRow
    ? [formatClassNumber(currentRow.classNumber), currentRow.className].filter(Boolean).join(" ")
    : "";

  return (
    <Box
      data-testid="live-desk-in-the-ring"
      sx={{
        // #3397 -- takes whatever height JUST FINISHED and NEXT UP leave, and
        // never scrolls (overflow hidden, content is budgeted to fit).
        flex: "1 1 0",
        minHeight: 0,
        overflow: "hidden",
        px: 2,
        py,
        bgcolor: "action.hover",
        // #3397 -- live is green everywhere in the redesign, and it does not blink.
        borderLeft: "6px solid",
        borderLeftColor: "success.main",
        borderBottom: 1,
        // `borderColor` (all four sides) must NOT follow `borderLeft` here: it would
        // overwrite the left border's colour with the divider grey, leaving the "in
        // the ring" accent bar visually indistinguishable from an ordinary divider.
        // `borderBottomColor` touches only the bottom edge it's meant for (#2146).
        borderBottomColor: "divider",
      }}
    >
      {/* #2446 -- the #2366 scribe-presence chips, ported from the retired
          RideRow. They share the title's line (right-aligned) rather than
          adding a row, because the In the Ring Now box never scrolls and
          every line counts. Only while a ride is up (no
          currentRow, no chip, same as RideRow's isCurrent gate), and
          additive: no presence rows renders exactly the bare title. */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
        <Typography
          data-testid="live-desk-in-the-ring-title"
          variant="overline"
          sx={{ letterSpacing: 1, flexShrink: 0, lineHeight: 2, color: "success.dark", fontWeight: 700 }}
        >
          {isEntering ? t("liveDesk.enteringRingTitle") : t("liveDesk.inTheRingTitle")}
        </Typography>
        {currentRow && sortedPresence.length > 0 ? (
          <Stack
            direction="row"
            spacing={0.5}
            sx={{ minWidth: 0, overflow: "hidden", justifyContent: "flex-start" }}
            data-testid="scribe-presence-row"
          >
            {sortedPresence.map((p) => (
              <Chip
                key={p.seat}
                size="small"
                color={p.quiet ? "error" : "success"}
                icon={p.quiet ? <ErrorOutlineIcon /> : <CheckCircleIcon />}
                data-testid="scribe-presence-chip"
                data-seat={p.seat}
                data-quiet={p.quiet ? "true" : "false"}
                aria-label={t(p.quiet ? "presence.quiet" : "presence.reporting", {
                  position: p.seat,
                  elapsed: formatElapsedSeconds(p.elapsedSeconds),
                })}
                label={t(p.quiet ? "liveDesk.presenceChipQuiet" : "liveDesk.presenceChipReporting", {
                  position: p.seat,
                  elapsed: formatElapsedSeconds(p.elapsedSeconds),
                })}
                sx={{ fontWeight: 700, flexShrink: 0 }}
              />
            ))}
          </Stack>
        ) : null}
        {currentRow && ordinal ? (
          <Typography
            data-testid="live-desk-ride-ordinal"
            variant="body2"
            color="text.secondary"
            sx={{ flexShrink: 0, ml: "auto !important", whiteSpace: "nowrap" }}
          >
            {t("liveDesk.rideOfTotal", { n: ordinal.n, total: ordinal.total })}
          </Typography>
        ) : null}
      </Stack>
      {/* #3596 -- the ring ride's active alert lives in this panel (not a
          full-width banner), with Dismiss. Alerts on other rides are the
          chip in the frame's alerts slot. */}
      {currentRow && currentRow.utilityEvents?.length > 0 ? (
        <Stack spacing={0.5} data-testid="live-desk-alerts" sx={{ mb: 0.5 }}>
          {currentRow.utilityEvents.map((event) => (
            <ActiveAlert key={event.id} row={currentRow} event={event} />
          ))}
        </Stack>
      ) : null}
      {!currentRow ? (
        <Box sx={{ display: "flex", alignItems: "center", height: "80%" }}>
          <Typography
            data-testid={ridesLoading || !ringStateKnown ? "live-desk-ring-state-loading" : "live-desk-ring-state"}
            role={ridesLoading || !ringStateKnown ? "status" : undefined}
            variant="h6"
            sx={{ fontWeight: 700 }}
          >
            {stateLine}
          </Typography>
        </Box>
      ) : (
        // #3169 -- the compact/small In the Ring Now box cannot hold every line plus
        // the 4px gaps between them once a ride carries the judges line AND
        // the full ride-detail block: the tail clipped. Each
        // line already carries its own line-height leading, so the gaps go to 0
        // below the normal tier rather than the content being dropped.
        <Stack spacing={0}>
          <Typography data-testid="live-desk-current-class" variant="body2" color="text.secondary" sx={tightLine}>
            {classLine}
          </Typography>
          {/* #3397 -- "182 - Penelope Cheesegardener" big, the division a chip. */}
          <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
            <Typography
              data-testid="live-desk-current-rider-line"
              variant={small ? "h6" : "h5"}
              noWrap
              sx={{ fontWeight: 800, lineHeight: 1.2, color: "text.primary", minWidth: 0 }}
            >
              <span data-testid="live-desk-current-competitor-number">{currentRow.competitorNumber}</span>
              {` - ${currentRow.riderName}`}
              <SectionTag section={currentRow.section} />
            </Typography>
            <Chip
              size="small"
              variant="outlined"
              data-testid="live-desk-current-division"
              label={currentRow.riderStatus || t("results.divisionOpen")}
              sx={{ flexShrink: 0, fontWeight: 700, height: 20 }}
            />
          </Stack>
          {committed && committed.entries.length > 0 ? (
            // #3169 -- ONE non-wrapping line, same idiom as the #2446 presence
            // row: five judges with real names wrapped to 2-3 lines and pushed
            // the detail block and times off the bottom of this
            // box, which never scrolls. The n/m summary goes FIRST and never shrinks;
            // each judge's check never shrinks; the "E: Name" label ellipsizes
            // from the tail when the row is crowded, and the fixed 560px column
            // keeps the position letter visible, so all five judges' committed
            // state stays readable (nothing is clipped off the right edge). The word "confirmed" is no longer printed per judge (the
            // check + bold already say it visually); it lives on the check
            // icon's accessible name instead.
            <Stack
              direction="row"
              spacing={0.75}
              alignItems="center"
              flexWrap="nowrap"
              sx={{ minWidth: 0, overflow: "hidden", justifyContent: "flex-start", whiteSpace: "nowrap" }}
              data-testid="live-desk-current-judges"
              data-confirmed-count={committed.confirmed}
              data-expected-count={committed.expected}
            >
              {committed.expected > 0 ? (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ flexShrink: 0, ...tightLine }}
                  data-testid="live-desk-committed-count"
                  aria-label={
                    denseCommitted
                      ? t("results.judgeCommittedCount", {
                          confirmed: committed.confirmed,
                          expected: committed.expected,
                        })
                      : undefined
                  }
                >
                  {denseCommitted
                    ? t("results.judgeCommittedCountShort", {
                        confirmed: committed.confirmed,
                        expected: committed.expected,
                      })
                    : t("results.judgeCommittedCount", {
                        confirmed: committed.confirmed,
                        expected: committed.expected,
                      })}
                </Typography>
              ) : null}
              {committed.entries.map((e) => {
                const label = e.name
                  ? t("liveDesk.judgeAtPosition", { position: e.position, name: e.name })
                  : e.position;
                return (
                  <Stack
                    key={e.position}
                    direction="row"
                    spacing={0.25}
                    alignItems="center"
                    sx={{ minWidth: 0, flexShrink: Number.isFinite(e.percentage) ? 0 : 1 }}
                    data-testid="live-desk-judge-committed"
                    data-position={e.position}
                    data-confirmed={e.committed ? "true" : "false"}
                  >
                    {e.committed ? (
                      <CheckCircleIcon
                        sx={{ color: "success.main", fontSize: "1rem", flexShrink: 0 }}
                        titleAccess={t("liveDesk.judgeConfirmed", { judge: label })}
                      />
                    ) : null}
                    {Number.isFinite(e.percentage) && e.name && committed.entries.length <= 3 ? (
                      // #3381 -- a scored judge keeps its name only on a small
                      // panel (<= 3 seats). On a 5-judge row the name tail
                      // crowded the score into its neighbour, so there a scored
                      // seat shows just "E: 71.234%" (seat letter never shrinks).
                      <>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          noWrap
                          sx={{ flexShrink: 0, fontWeight: 700, ...tightLine }}
                        >
                          {`${e.position}:`}
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          noWrap
                          sx={{ minWidth: 0, fontWeight: 700, ...tightLine }}
                        >
                          {e.name}
                        </Typography>
                      </>
                    ) : (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        noWrap
                        sx={{
                          minWidth: 0,
                          ...(e.committed ? { fontWeight: 700 } : null),
                          ...(Number.isFinite(e.percentage) ? { flexShrink: 0 } : null),
                          ...tightLine,
                        }}
                      >
                        {Number.isFinite(e.percentage) ? `${e.position}:` : label}
                      </Typography>
                    )}
                    {Number.isFinite(e.percentage) ? (
                      <Typography
                        variant="body2"
                        noWrap
                        sx={{ flexShrink: 0, fontWeight: 700, ml: 0.25, ...tightLine }}
                        data-testid="live-desk-judge-partial-score"
                      >
                        {t("liveDesk.judgePartialScore", { value: formatComposite(e.percentage) })}
                      </Typography>
                    ) : null}
                  </Stack>
                );
              })}
            </Stack>
          ) : judgesText ? (
            <Typography data-testid="live-desk-current-judges" variant="body2" color="text.secondary" sx={tightLine}>
              {judgesText}
            </Typography>
          ) : null}
          {/* The shared ride-detail block (#3395), with the horse name big and
              black right after the rider location line. */}
          <RideDetailBlock
            detail={currentRow.detail}
            sx={detailSx}
            afterLocation={
              currentRow.horseName ? (
                <Typography
                  data-testid="live-desk-current-horse-name"
                  variant={small ? "subtitle1" : "h6"}
                  noWrap
                  sx={{ fontWeight: 800, lineHeight: 1.25, color: "common.black" }}
                >
                  {currentRow.horseName}
                </Typography>
              ) : null
            }
          />
          {/* #3398 -- the box is too short at the compact and small tiers for the entered/
              elapsed line to fit under a full detail set, so it prints at the normal tier only. */}
          {compact ? null : (
            <Typography data-testid="live-desk-current-times" variant="body2" color="text.secondary" sx={tightLine}>
              {[
                currentRow.scheduledTime ? t("liveDesk.entered", { time: formatTime(currentRow.scheduledTime, currentRow.scheduledTimeZone) }) : null,
                elapsedSeconds != null
                  ? t("liveDesk.inTheRingElapsed", { elapsed: formatElapsedSeconds(elapsedSeconds) })
                  : null,
              ]
                .filter(Boolean)
                .join(" · ")}
            </Typography>
          )}
        </Stack>
      )}
    </Box>
  );
}

// #3397 -- per-tier fontSize/lineHeight for NextUpRow's lines (the box is no
// longer a fixed-height scroll window, so these only keep the three rows tight
// at the shorter tiers).
const NEXT_UP_LINE_SX = {
  normal: { line1: undefined, line2: undefined },
  compact: {
    line1: { fontSize: "0.8125rem", lineHeight: 1.15 },
    line2: { fontSize: "0.6875rem", lineHeight: 1.15 },
  },
  small: {
    line1: { fontSize: "0.6875rem", lineHeight: 1.05 },
    line2: { fontSize: "0.625rem", lineHeight: 1.05 },
  },
};

// #3448 -- a break in NEXT UP: same dashed orange + fork-and-knife look as the
// Stream's BreakRow, sized to a NEXT UP row. Never marked differentClass.
function NextUpBreakRow({ row, index, tier, t }) {
  const { line1: line1Sx } = NEXT_UP_LINE_SX[tier] ?? NEXT_UP_LINE_SX.normal;
  return (
    <Stack
      data-testid={`live-desk-next-up-row-${index}`}
      data-kind="break"
      data-different-class="false"
      direction="row"
      spacing={1}
      alignItems="center"
      sx={{
        flexShrink: 0,
        overflow: "hidden",
        my: 0.25,
        px: 1,
        py: 0.25,
        border: "1px dashed",
        borderColor: colors.foxOrange,
        borderRadius: 1,
      }}
    >
      <RestaurantIcon sx={{ color: colors.foxOrange, fontSize: "1rem" }} />
      <Typography
        data-testid={`live-desk-next-up-time-${index}`}
        variant="body2"
        sx={{ fontWeight: 700, flexShrink: 0, ...line1Sx }}
        noWrap
      >
        {formatTime(row.scheduledTime, row.scheduledTimeZone)}
      </Typography>
      <Typography
        data-testid={`live-desk-next-up-break-label-${index}`}
        variant="body2"
        sx={{ fontWeight: 800, minWidth: 0, ...line1Sx }}
        noWrap
      >
        {row.label || t("oog.breakLabel")}
      </Typography>
    </Stack>
  );
}

// #3397 -- one NEXT UP ride: "725 Cal Brennan on Midnight Ledger" (bib, rider on
// horse), then a small "3:04 PM · Open" line (time, then the rider's division).
// No class name, EXCEPT a ride in a different class from the live one, which
// gets a small class line above it. An exception label (scratched / eliminated /
// no show) rides on the small line after the division. Every line is noWrap so
// nothing wraps into a neighbouring row.
function NextUpRow({ row, index, tier, t }) {
  if (row.kind === ROW_KIND.BREAK) return <NextUpBreakRow row={row} index={index} tier={tier} t={t} />;
  // #3399 -- a terminated ride shows a red term-reason chip, never a strikethrough.
  const chipReason = termChipReason(row);
  const exceptionLabel = exceptionStatusLabel(row, t);
  const { line1: line1Sx, line2: line2Sx } = NEXT_UP_LINE_SX[tier] ?? NEXT_UP_LINE_SX.normal;
  const classLabel = [formatClassNumber(row.classNumber), row.className].filter(Boolean).join(" ");
  const division = row.riderStatus || t("results.divisionOpen");
  const riderOnHorse = row.horseName
    ? t("liveDesk.riderOnHorse", { rider: row.riderName, horse: row.horseName })
    : row.riderName;

  return (
    <Stack
      data-testid={`live-desk-next-up-row-${index}`}
      data-different-class={String(Boolean(row.differentClass))}
      spacing={0}
      sx={{ flexShrink: 0, overflow: "hidden", py: 0.25 }}
    >
      {row.differentClass && classLabel ? (
        <Typography
          data-testid={`live-desk-next-up-row-${index}-class`}
          variant="caption"
          color="text.secondary"
          noWrap
          sx={{ fontWeight: 700, ...line2Sx }}
        >
          {classLabel}
        </Typography>
      ) : null}
      <Stack direction="row" spacing={1} alignItems="baseline" sx={{ minWidth: 0 }}>
        <Typography variant="body2" sx={{ fontWeight: 700, minWidth: 28, flexShrink: 0, ...line1Sx }}>
          {row.competitorNumber}
        </Typography>
        <Typography variant="body2" sx={{ flexGrow: 1, minWidth: 0, ...line1Sx }} noWrap>
          {riderOnHorse}
          <SectionTag section={row.section} />
        </Typography>
      </Stack>
      <Stack direction="row" spacing={1} alignItems="baseline" sx={{ minWidth: 0, pl: "36px" }}>
        <Typography
          data-testid={`live-desk-next-up-time-${index}`}
          variant="caption"
          color="text.secondary"
          noWrap
          sx={{ flexShrink: 0, ...line2Sx }}
        >
          {formatTime(row.scheduledTime, row.scheduledTimeZone)}
        </Typography>
        <Typography
          data-testid={`live-desk-next-up-division-${index}`}
          variant="caption"
          color="text.secondary"
          noWrap
          sx={{ minWidth: 0, ...line2Sx }}
        >
          {`· ${division}`}
        </Typography>
        {chipReason ? (
          <TermReasonChip
            data-testid={`live-desk-next-up-term-${index}`}
            termReason={chipReason}
            termLabels={termChipLabels(t)}
            sx={{
              fontWeight: 700,
              flexShrink: 0,
              alignSelf: "center",
              height: 18,
              ...line2Sx,
              "& .MuiChip-label": { px: 0.75 },
            }}
          />
        ) : exceptionLabel ? (
          <Typography variant="caption" sx={{ fontWeight: 700, flexShrink: 0, ...line2Sx }} noWrap>
            {exceptionLabel}
          </Typography>
        ) : null}
      </Stack>
    </Stack>
  );
}

// #3397 -- natural height (flexShrink 0), exactly the rows it is given (the
// caller caps them at NEXT_UP_COUNT): no scroll window, no fixed row count.
function NextUpSlot({ nextUpRows, py, tier, loading, t }) {
  return (
    <Box
      data-testid="live-desk-next-up"
      sx={{ flexShrink: 0, overflow: "hidden", display: "flex", flexDirection: "column", px: 2, py }}
    >
      <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 1, flexShrink: 0, lineHeight: 2 }}>
        {t("liveDesk.nextUpTitle")}
      </Typography>
      <Box data-testid="live-desk-next-up-list">
        {loading && nextUpRows.length === 0 ? (
          <Typography data-testid="live-desk-next-up-loading" role="status" variant="body2" color="text.secondary">
            {t("liveDesk.nextUpLoading")}
          </Typography>
        ) : null}
        <Stack spacing={0}>
          {nextUpRows.map((row, i) => (
            <NextUpRow key={row.id} row={row} index={i} tier={tier} t={t} />
          ))}
        </Stack>
      </Box>
    </Box>
  );
}

function LiveDesk() {
  const { t } = useTranslation("announcer");
  const { selectedRing } = useAnnouncerContext();
  const {
    rows,
    currentRideId,
    ringTruth,
    resultsByClassId,
    ringState,
    ringStateKnown,
    liveNow,
    classes,
    regionLoading,
    todayKey,
    hasFetchError,
    presenceRows,
  } = useRingData();
  // AC13 — see NORMAL_TOTAL_HEIGHT comment above: measure the band's OWN
  // rendered height rather than guessing from window height, so whatever
  // else is stacked above it (StatusBar today, anything else later) is
  // automatically accounted for. Starts `null` (treated as "fits normally")
  // before the first measurement lands, same as DaySpine's own ladder, so a
  // fixture that fits never flashes compressed.
  const containerRef = useRef(null);
  const [availableHeight, setAvailableHeight] = useState(null);
  useEffect(() => {
    const el = containerRef.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver((entriesList) => {
      const entry = entriesList[0];
      if (entry) setAvailableHeight(entry.contentRect.height);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  const compact = availableHeight != null && availableHeight < NORMAL_TOTAL_HEIGHT;
  // #2447 -- SMALL is strictly a subset of compact (COMPACT_TOTAL_HEIGHT <
  // NORMAL_TOTAL_HEIGHT), so `compact` alone still gates the h4/h2 typography
  // swap in InTheRingSlot below; SMALL only narrows which pixel preset the
  // three slots use.
  const small = availableHeight != null && availableHeight < COMPACT_TOTAL_HEIGHT;
  const slot1Height = small ? SLOT1_HEIGHT_SMALL : compact ? SLOT1_HEIGHT_COMPACT : SLOT1_HEIGHT;
  // #2447 -- SMALL also trims each box's own `py` padding (not just the
  // height presets above): content does not shrink with the tier, only
  // padding is reclaimable.
  const slotPy = small ? 0.5 : 1.5;
  const nextUpPy = small ? 0.5 : 1;
  // #2452 -- JustFinishedSlot's own score/placing typography needs all three
  // tiers, not just the compact/normal split InTheRingSlot's swaps use, so it
  // gets a dedicated string rather than reusing `compact` alone. NextUpRow's
  // typography reuses this same string (see NEXT_UP_LINE_SX).
  const justFinishedTier = small ? "small" : compact ? "compact" : "normal";

  // #2587 (TRI-241 judge finding B) — ringTruth.rideId is the ring's OWN
  // stored occupant, resolved verbatim by resolveRingTruth (null unless the
  // ring is genuinely RUNNING with a class). Passed through as an EXPLICIT
  // "this is the ride that just finished" hint rather than letting
  // findLiveDeskAnchor infer it by scanning backward for "the last row
  // that's not PENDING" — a later SCRATCHED row in running order would win
  // that scan instead (liveDesk.js's own #2587 comment on findLiveDeskAnchor
  // has the full mechanism). Only consulted when currentRideId doesn't
  // resolve, which the guard in orderOfGo.js's assignRoles makes true
  // exactly when this same ride just reached CONFIRMED/FINAL.
  // #3397 -- NEXT UP is exactly the next NEXT_UP_COUNT (3) rides, fewer when
  // fewer are left; this reverses #2624's scrolling lookahead.
  const nextUpCount = small ? NEXT_UP_COUNT_SMALL : compact ? NEXT_UP_COUNT_SHORT : NEXT_UP_COUNT;
  const { currentRow, justFinishedRow, nextUpRows } = useMemo(
    () =>
      selectLiveDeskSequence({
        rows,
        currentRideId,
        justFinishedRideId: ringTruth?.rideId,
        nextUpCount,
      }),
    [rows, currentRideId, ringTruth, nextUpCount],
  );

  // #2585 (TRI-238) — the class BETWEEN_RIDES should name, read off the SAME
  // ringTruth.classId the day spine's RUNNING marker already uses
  // (resolveLiveClassId, #2131) against the classes array useRingData()
  // already provides. "" when there's no live class to name (BREAK/
  // END_OF_DAY wipe classId; a genuinely idle ring never had one).
  const activeClassLabel = useMemo(
    () => findActiveClassLabel(classes, ringTruth?.classId),
    [classes, ringTruth],
  );

  const ringHasRidesLeft = useMemo(() => ringHasRidesLeftToday(rows, todayKey), [rows, todayKey]);

  return (
    <Box
      ref={containerRef}
      data-testid="live-desk"
      sx={{ height: "100%", overflow: "hidden", display: "flex", flexDirection: "column" }}
    >
      <JustFinishedSlot
        row={justFinishedRow}
        resultsByClassId={resultsByClassId}
        height={slot1Height}
        py={slotPy}
        tier={justFinishedTier}
        loading={regionLoading.rides || regionLoading.ringState}
        t={t}
      />
      <InTheRingSlot
        currentRow={currentRow}
        ringState={ringState}
        selectedRing={selectedRing}
        liveNow={liveNow}
        nextUpRows={nextUpRows}
        rows={rows}
        activeClassLabel={activeClassLabel}
        py={slotPy}
        compact={compact}
        small={small}
        ridesLoading={regionLoading.rides}
        ringStateKnown={ringStateKnown || !selectedRing}
        ringHasRidesLeft={ringHasRidesLeft}
        hasFetchError={hasFetchError}
        presenceRows={presenceRows}
        resultsByClassId={resultsByClassId}
        t={t}
      />
      <NextUpSlot
        nextUpRows={nextUpRows}
        py={nextUpPy}
        tier={justFinishedTier}
        loading={regionLoading.rides}
        t={t}
      />
    </Box>
  );
}

export default LiveDesk;
